/**
 * ChatPage — Human ↔ AI Brain chat interface
 *
 * - Left panel:  session list (create / select / delete)
 * - Right panel: chat window with streaming AI replies
 * - Full attachment support (R2 presigned upload)
 * - Rich widget rendering (approval, table, form, list…)
 * - Mobile: session list slides in as a bottom drawer / overlay
 */

import { useState, useEffect, useRef, useCallback, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { api, apiFetch, BASE, getToken } from '@/lib/api';
import { useToast } from '@/lib/toast';
import { useRenderMode } from '@/hooks/useRenderMode';
import { usePageParams } from '@/hooks/usePageParams';
import { useNavigate } from 'react-router-dom';
import {
  useChat,
  type ChatSession,
  type ChatMessage,
  type ChatMode,
  type ChatActionResult,
  type ChatWidget,
  type ApprovalRecommendation,
  type LayerInvocationEnvelope,
} from '@/hooks/useChat';
import { ChatMessageWidget } from '@/components/chat/ChatMessageWidget';
import { MarkdownRenderer } from '@/components/chat/MarkdownRenderer';
import { ThinkingSteps } from '@/components/chat/ThinkingSteps';
import { AutonomousProgress } from '@/components/chat/AutonomousProgress';
import { trackEvent } from '@/lib/analytics';
import { postEntityThreadNote } from '@/lib/entity-thread-notes';
import { HmsJobAuditModal } from '@/components/chat/HmsJobAuditModal';
import { useChatContextStore } from '@/stores/chat-context-store';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Plus, Trash2, MessageSquare, Send, Loader2,
  Paperclip, FileText, X, Menu, ChevronLeft, Bot, User, Share2, Copy, Check, Clock,
  Mic, MicOff, Volume2, VolumeX, Languages, Square,
  ChevronDown, ChevronUp, Settings2, Zap, Search, Archive, ArchiveRestore, ShieldAlert,
} from 'lucide-react';
import { useVoiceInput } from '@/hooks/useVoiceInput';
import { useTextToSpeech } from '@/hooks/useTextToSpeech';
import { useGeminiLiveSession } from '@/hooks/useGeminiLiveSession';
import { useTranslate, TRANSLATE_LANGUAGES, type TranslateLang, type TranslationEntry } from '@/hooks/useTranslate';
import { getVoiceInputCapability } from '@boox/ui-foundation';

const QUICK_PROMPTS_STORAGE_KEY = 'boox_chat_quick_prompts';
const ARCHIVED_SESSIONS_KEY = 'boox_chat_archived_sessions';

// ─── Quick prompt types & helpers ─────────────────

interface QuickPrompt {
  id: string;
  label: string;
  prompt: string;
  autoExecute: boolean;
}

const DEFAULT_QUICK_PROMPTS: QuickPrompt[] = [
  { id: 'hms-today', label: '📋 Today summary', prompt: "Give me today's container haulage summary — active jobs, trip status, any exceptions.", autoExecute: true },
  { id: 'hms-tomorrow', label: '📅 Tomorrow schedule', prompt: "Show me tomorrow's scheduled containers and trips.", autoExecute: true },
];

function loadQuickPrompts(): QuickPrompt[] {
  try {
    const saved = localStorage.getItem(QUICK_PROMPTS_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch { /* ignore */ }
  return DEFAULT_QUICK_PROMPTS;
}

function saveQuickPrompts(prompts: QuickPrompt[]) {
  try {
    localStorage.setItem(QUICK_PROMPTS_STORAGE_KEY, JSON.stringify(prompts));
  } catch { /* ignore */ }
}

function getNowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function getElapsedMs(startedAt: number | null): number | null {
  if (startedAt == null) return null;
  return Math.max(0, Math.round(getNowMs() - startedAt));
}

interface WorkspaceAction {
  id: string;
  label: string;
  tone?: 'default' | 'warn';
}

// ─── Session list panel ───────────────────────────

function SessionList({
  sessions,
  activeId,
  onSelect,
  onNew,
  onDelete,
  onArchive,
  onRestore,
  archivedSessions,
  loading,
}: {
  sessions: ChatSession[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
  onArchive: (id: string) => void;
  onRestore: (id: string) => void;
  archivedSessions: ChatSession[];
  loading: boolean;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const filtered = search.trim()
    ? sessions.filter(s => s.subject.toLowerCase().includes(search.trim().toLowerCase()))
    : sessions;

  const filteredArchived = search.trim()
    ? archivedSessions.filter(s => s.subject.toLowerCase().includes(search.trim().toLowerCase()))
    : archivedSessions;

  return (
    <div className="flex flex-col h-full">
      {/* Compact header: search input + new button */}
      <div className="px-2 py-2 border-b shrink-0 space-y-0">
        <div className="flex items-center gap-1">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('chat.searchPlaceholder', 'Search...')}
              className="w-full rounded-md border bg-background pl-7 pr-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
            />
            {search && (
              <button
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setSearch('')}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={onNew} title={t('chat.newChat', 'New chat')}>
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-1">
        {loading && (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}
        {!loading && sessions.length === 0 && archivedSessions.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-8 px-4">
            {t('chat.noSessions', 'No conversations yet.\nClick + to start.')}
          </p>
        )}
        {!loading && sessions.length > 0 && filtered.length === 0 && search.trim() && !filteredArchived.length && (
          <p className="text-xs text-muted-foreground text-center py-8 px-4">
            {t('chat.noSearchResults', 'No matching conversations')}
          </p>
        )}
        {filtered.map(s => (
          <div
            key={s.id}
            className={cn(
              'group flex items-start gap-2 px-3 py-2.5 cursor-pointer rounded-md mx-1',
              'hover:bg-muted/70 transition-colors',
              activeId === s.id && 'bg-muted',
            )}
            onClick={() => onSelect(s.id)}
          >
            <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">{s.subject}</p>
              <p className="text-[10px] text-muted-foreground">
                {new Date(s.last_message_at ?? s.created_at).toLocaleDateString()}
              </p>
            </div>
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                className="p-1 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
                onClick={e => { e.stopPropagation(); onArchive(s.id); }}
                title={t('chat.archive', 'Archive')}
              >
                <Archive className="h-3 w-3" />
              </button>
              <button
                className="p-1 rounded-md text-muted-foreground hover:bg-muted hover:text-destructive transition-all"
                onClick={e => { e.stopPropagation(); onDelete(s.id); }}
                title={t('chat.delete', 'Delete')}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          </div>
        ))}

        {/* Archived section */}
        {archivedSessions.length > 0 && (
          <>
            <button
              className="flex items-center gap-1.5 w-full px-3 py-2 mt-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setShowArchived(!showArchived)}
            >
              <Archive className="h-3 w-3" />
              <span>{t('chat.archivedCount', 'Archived ({{count}})', { count: archivedSessions.length })}</span>
              {showArchived ? <ChevronDown className="h-3 w-3 ml-auto" /> : <ChevronUp className="h-3 w-3 ml-auto" />}
            </button>
            {showArchived && (showArchived ? filteredArchived : []).map(s => (
              <div
                key={s.id}
                className="group flex items-start gap-2 px-3 py-2 cursor-pointer rounded-md mx-1 hover:bg-muted/70 transition-colors opacity-60"
                onClick={() => { onRestore(s.id); onSelect(s.id); }}
              >
                <Archive className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{s.subject}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {new Date(s.last_message_at ?? s.created_at).toLocaleDateString()}
                  </p>
                </div>
                <button
                  className="p-1 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-all opacity-0 group-hover:opacity-100"
                  onClick={e => { e.stopPropagation(); onRestore(s.id); }}
                  title={t('chat.restore', 'Restore')}
                >
                  <ArchiveRestore className="h-3 w-3" />
                </button>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Attachment image with lazy URL loading ────────

function AttachmentImage({ storageKey, filename }: { storageKey: string; filename: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get<{ url?: string }>(`/storage/url?key=${encodeURIComponent(storageKey)}`)
      .then((r) => {
        if (!cancelled && r?.url) setUrl(r.url);
        else if (!cancelled) setError(true);
      })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [storageKey]);

  if (error || !url) {
    return (
      <Badge variant="secondary" className="text-[10px] max-w-[180px] truncate">
        <Paperclip className="mr-1 h-2.5 w-2.5" />
        {filename}
      </Badge>
    );
  }

  return (
    <>
      <img
        src={url}
        alt={filename}
        onClick={() => setExpanded(true)}
        className="max-w-[200px] max-h-[160px] rounded-lg border border-border/50 cursor-pointer hover:opacity-90 transition-opacity object-cover"
      />
      {expanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
          onClick={() => setExpanded(false)}
        >
          <img src={url} alt={filename} className="max-w-[90vw] max-h-[85vh] rounded-lg shadow-2xl" />
        </div>
      )}
    </>
  );
}

function PdfAttachmentBadge({ storageKey, filename }: { storageKey: string; filename: string }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.get<{ url?: string }>(`/storage/url?key=${encodeURIComponent(storageKey)}`)
      .then((r) => {
        if (!cancelled && r?.url) setUrl(r.url);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [storageKey]);

  const inner = (
    <>
      <FileText className="mr-1 h-2.5 w-2.5 shrink-0" />
      <span className="truncate">{filename}</span>
    </>
  );

  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-secondary px-2 py-0.5 text-[10px] max-w-[200px] text-secondary-foreground hover:bg-accent transition-colors no-underline"
        title={`Open ${filename}`}
      >
        {inner}
      </a>
    );
  }

  return (
    <Badge
      variant="secondary"
      className="text-[10px] max-w-[200px] opacity-70"
      title={filename}
    >
      {inner}
    </Badge>
  );
}

function RecommendationToneBadge({
  tone,
  children,
}: {
  tone: 'default' | 'good' | 'warn' | 'danger';
  children: ReactNode;
}) {
  const className = cn(
    'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize',
    tone === 'good' && 'border-emerald-200 bg-emerald-50 text-emerald-700',
    tone === 'warn' && 'border-amber-200 bg-amber-50 text-amber-700',
    tone === 'danger' && 'border-rose-200 bg-rose-50 text-rose-700',
    tone === 'default' && 'border-slate-200 bg-white/80 text-slate-600',
  );

  return <span className={className}>{children}</span>;
}

function ApprovalRecommendationCard({
  recommendation,
  messageId,
  conversationId,
  onAction,
}: {
  recommendation: ApprovalRecommendation;
  messageId: string;
  conversationId?: string | null;
  onAction?: (actionType: string, payload: unknown) => Promise<unknown> | void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const [creatingApproval, setCreatingApproval] = useState(false);
  const [createdApprovalId, setCreatedApprovalId] = useState<string | null>(null);
  const stanceTone =
    recommendation.finalStance === 'approve'
      ? 'good'
      : recommendation.finalStance === 'revise'
        ? 'warn'
        : 'danger';

  const urgencyTone =
    recommendation.urgency === 'high'
      ? 'danger'
      : recommendation.urgency === 'medium'
        ? 'warn'
        : 'default';

  const openApproval = () => {
    if (!createdApprovalId) return;
    navigate(`/e/approval-requests/${createdApprovalId}`);
  };

  const openQueue = () => {
    navigate('/e/approval-requests');
  };

  return (
    <div className="rounded-2xl border border-amber-200/70 bg-amber-50/70 px-3.5 py-3 text-sm text-amber-950 shadow-sm">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
          <ShieldAlert className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-700">
              {recommendation.recommended ? 'Approval Recommended' : 'Approval Review'}
            </span>
            <RecommendationToneBadge tone={stanceTone}>
              {recommendation.finalStance}
            </RecommendationToneBadge>
            <RecommendationToneBadge tone={urgencyTone}>
              {recommendation.urgency}
            </RecommendationToneBadge>
            <RecommendationToneBadge tone="default">
              {recommendation.taskCategory}
            </RecommendationToneBadge>
            <RecommendationToneBadge tone="default">
              {Math.round(recommendation.confidence * 100)}% confidence
            </RecommendationToneBadge>
          </div>

          <p className="text-sm leading-relaxed text-amber-950/90">
            {recommendation.reason}
          </p>

          <div className="rounded-xl border border-amber-200/70 bg-white/70 px-3 py-2">
            <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-amber-700">
              Recommended Action
            </div>
            <div className="mt-1 text-sm text-slate-800">
              {recommendation.recommendedAction}
            </div>
          </div>

          {recommendation.risks.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {recommendation.risks.map((risk) => (
                <Badge
                  key={risk}
                  variant="outline"
                  className="border-amber-300/80 bg-white/80 text-[10px] text-amber-800"
                >
                  {risk}
                </Badge>
              ))}
            </div>
          )}

          {(recommendation.recommended || createdApprovalId) && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {!createdApprovalId ? (
                <>
                  <Button
                    size="sm"
                    className="h-8"
                    disabled={creatingApproval}
                    onClick={async () => {
                      if (!onAction) {
                        toast.error('Approval queue is unavailable.');
                        return;
                      }

                      setCreatingApproval(true);
                      try {
                        const result = await onAction('request_approval', {
                          title: `Approval required for ${recommendation.taskCategory}`,
                          description: recommendation.reason,
                          urgency: recommendation.urgency,
                          requesterRole: 'chat_user',
                          reviewerRole: recommendation.urgency === 'high' ? 'supervisor' : 'manager',
                          escalationRole: recommendation.urgency === 'high' ? 'admin' : 'supervisor',
                          payload: {
                            source: 'chat_recommendation',
                            source_message_id: messageId,
                            source_conversation_id: conversationId,
                            task_category: recommendation.taskCategory,
                            final_stance: recommendation.finalStance,
                            consensus_level: recommendation.consensusLevel,
                            confidence: recommendation.confidence,
                            recommended_action: recommendation.recommendedAction,
                            risks: recommendation.risks,
                            reasons: recommendation.reasons,
                          },
                        }) as { id?: string } | undefined;

                        if (result?.id) {
                          setCreatedApprovalId(result.id);
                          toast.success('Approval request added to queue.');
                        } else {
                          toast.success('Approval request submitted.');
                        }
                      } catch (err) {
                        toast.error(err instanceof Error ? err.message : 'Failed to create approval request');
                      } finally {
                        setCreatingApproval(false);
                      }
                    }}
                  >
                    {creatingApproval ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <ShieldAlert className="mr-1.5 h-3.5 w-3.5" />}
                    Send to Approval Queue
                  </Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={openQueue}>
                    Open Queue
                  </Button>
                </>
              ) : (
                <>
                  <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700">
                    Queued
                  </Badge>
                  <Button size="sm" variant="outline" className="h-8" onClick={openApproval}>
                    Open Request
                  </Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={openQueue}>
                    Open Queue
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Gen5LayerInvocationCard({
  envelope,
}: {
  envelope: LayerInvocationEnvelope;
}) {
  const taskCategory = envelope.taskCategory.category;
  const executionMode = envelope.harness.executionMode ?? 'UNSPECIFIED';
  const confidence = Math.round(envelope.taskCategory.confidence * 100);
  const auditLabel = envelope.harness.auditRequired ? 'audit tracked' : 'auto';

  return (
    <div className="flex max-w-full items-center gap-1.5 overflow-hidden px-1 text-[11px] leading-5 text-muted-foreground">
      <span className="shrink-0">Runtime</span>
      <span className="text-muted-foreground/50">·</span>
      <span className="truncate">{taskCategory}</span>
      <span className="text-muted-foreground/50">·</span>
      <span className="shrink-0">{envelope.harness.riskClass}</span>
      <span className="text-muted-foreground/50">·</span>
      <span className="truncate">{executionMode}</span>
      <span className="text-muted-foreground/50">·</span>
      <span className="shrink-0">{auditLabel}</span>
      <span className="text-muted-foreground/50">·</span>
      <span className="shrink-0">{confidence}%</span>
    </div>
  );
}

// ─── Message bubble ────────────────────────────────

function MessageBubble({
  msg,
  streaming,
  conversationId,
  onAction,
  onSpeak,
  onStopSpeak,
  isSpeaking,
  isSpeakLoading,
  translation,
  onTranslate,
  isTranslating,
  onClearTranslation,
}: {
  msg: ChatMessage;
  streaming?: boolean;
  conversationId?: string | null;
  onAction?: (type: string, payload: unknown) => Promise<unknown> | void;
  onSpeak?: (text: string, msgId: string) => void;
  onStopSpeak?: () => void;
  isSpeaking?: boolean;
  isSpeakLoading?: boolean;
  translation?: TranslationEntry;
  onTranslate?: (msgId: string, text: string, lang: TranslateLang) => void;
  isTranslating?: boolean;
  onClearTranslation?: (msgId: string) => void;
}) {
  const isUser = msg.role === 'user';
  const [showLangPicker, setShowLangPicker] = useState(false);

  const hasText = msg.content && msg.content.trim().length > 0 && !msg.widgetData;

  return (
    <div className={cn('flex group', isUser ? 'justify-end' : 'justify-start')}>
      <div className={cn('space-y-1', isUser ? 'max-w-[80%] items-end' : 'max-w-[95%]')}>
        {/* Autonomous execution progress (above thinking steps) */}
        {!isUser && msg.autonomousState && (
          <AutonomousProgress state={msg.autonomousState} streaming={streaming} />
        )}

        {/* Thinking steps (above bubble) */}
        {!isUser && msg.thinkingSteps && msg.thinkingSteps.length > 0 && (
          <ThinkingSteps steps={msg.thinkingSteps} streaming={streaming} />
        )}

        {!isUser && msg.layerInvocationEnvelope && (
          <Gen5LayerInvocationCard envelope={msg.layerInvocationEnvelope} />
        )}

        {!isUser && msg.approvalRecommendation && (
          <ApprovalRecommendationCard
            recommendation={msg.approvalRecommendation}
            messageId={msg.id}
            conversationId={conversationId}
            onAction={onAction}
          />
        )}

        {/* Widget OR text */}
        {!isUser && msg.widgetData ? (
          <ChatMessageWidget widget={msg.widgetData} onAction={onAction} />
        ) : (
          <div className={cn(
            'rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed break-words',
            isUser
              ? 'bg-primary text-primary-foreground rounded-tr-sm whitespace-pre-wrap'
              : 'bg-muted rounded-tl-sm',
          )}>
            {isUser ? (
              msg.content
            ) : (
              <MarkdownRenderer content={msg.content} />
            )}
            {streaming && !isUser && (
              <span className="ml-1 inline-block h-3.5 w-0.5 animate-pulse bg-current opacity-70 align-middle" />
            )}
          </div>
        )}

        {/* Translation display */}
        {translation && (
          <div className={cn(
            'rounded-xl px-3 py-2 text-xs leading-relaxed border border-dashed',
            isUser ? 'border-primary/30 bg-primary/5' : 'border-muted-foreground/20 bg-muted/50',
          )}>
            <div className="flex items-center gap-1 mb-1">
              <Languages className="h-3 w-3 text-muted-foreground" />
              <span className="text-[10px] text-muted-foreground font-medium">
                {TRANSLATE_LANGUAGES.find(l => l.code === translation.lang)?.label}
              </span>
              <button
                className="ml-auto text-muted-foreground hover:text-foreground"
                onClick={() => onClearTranslation?.(msg.id)}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
            {translation.text}
          </div>
        )}

        {/* Attachments */}
        {msg.attachmentKeys && msg.attachmentKeys.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-1.5">
            {msg.attachmentKeys.map(k => {
              const filename = k.split('/').pop() ?? k;
              const ext = filename.split('.').pop()?.toLowerCase() ?? '';
              const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);

              return isImage ? (
                <AttachmentImage key={k} storageKey={k} filename={filename} />
              ) : (
                <PdfAttachmentBadge key={k} storageKey={k} filename={filename} />
              );
            })}
          </div>
        )}

        {/* Time + action buttons */}
        <div className={cn('flex items-center gap-1 px-1', isUser && 'flex-row-reverse')}>
          <p className="text-[10px] text-muted-foreground">
            {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>

          {/* TTS button */}
          {hasText && !streaming && onSpeak && (
            <button
              className={cn(
                'p-0.5 rounded text-muted-foreground transition-all',
                'hover:text-foreground hover:bg-muted',
                (isSpeaking || isSpeakLoading) && 'text-primary',
              )}
              onClick={() => isSpeaking ? onStopSpeak?.() : onSpeak(msg.content, msg.id)}
              title={isSpeaking ? 'Stop' : 'Read aloud'}
            >
              {isSpeakLoading
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : isSpeaking
                  ? <VolumeX className="h-3 w-3" />
                  : <Volume2 className="h-3 w-3" />}
            </button>
          )}

          {/* Translate button */}
          {hasText && !streaming && onTranslate && (
            <div className="relative">
              <button
                className={cn(
                  'p-0.5 rounded text-muted-foreground transition-all',
                  'hover:text-foreground hover:bg-muted',
                  (isTranslating || translation) && 'text-blue-500',
                )}
                onClick={() => setShowLangPicker(!showLangPicker)}
                title="Translate"
              >
                {isTranslating
                  ? <Loader2 className="h-3 w-3 animate-spin" />
                  : <Languages className="h-3 w-3" />}
              </button>
              {showLangPicker && (
                <div className={cn(
                  'absolute z-30 mt-1 rounded-lg border bg-background shadow-lg p-1 min-w-[120px]',
                  isUser ? 'right-0' : 'left-0',
                )}>
                  {TRANSLATE_LANGUAGES.map(lang => (
                    <button
                      key={lang.code}
                      className="w-full text-left px-2 py-1 text-xs rounded hover:bg-muted transition-colors"
                      onClick={() => {
                        setShowLangPicker(false);
                        onTranslate(msg.id, msg.content, lang.code);
                      }}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Quick prompt management modal ────────────────

function QuickPromptManager({
  prompts,
  onSave,
  onClose,
}: {
  prompts: QuickPrompt[];
  onSave: (prompts: QuickPrompt[]) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState<QuickPrompt[]>(() => [...prompts]);
  const [editIdx, setEditIdx] = useState<number | null>(null);

  const handleAdd = () => {
    const newItem: QuickPrompt = {
      id: `qp-${Date.now()}`,
      label: '',
      prompt: '',
      autoExecute: false,
    };
    setItems(prev => [...prev, newItem]);
    setEditIdx(items.length);
  };

  const handleDelete = (idx: number) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
    if (editIdx === idx) setEditIdx(null);
    else if (editIdx !== null && editIdx > idx) setEditIdx(editIdx - 1);
  };

  const handleUpdate = (idx: number, field: keyof QuickPrompt, value: string | boolean) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const handleSave = () => {
    const valid = items.filter(i => i.label.trim() && i.prompt.trim());
    onSave(valid);
    onClose();
  };

  return (
    <>
      <div className="absolute inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-xl border bg-background shadow-2xl max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
            <div className="flex items-center gap-2">
              <Settings2 className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold">{t('chat.managePrompts', 'Manage Quick Prompts')}</span>
            </div>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onClose}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {items.map((item, idx) => (
              <div key={item.id} className="rounded-lg border p-2.5 space-y-2">
                {editIdx === idx ? (
                  <>
                    <input
                      value={item.label}
                      onChange={(e) => handleUpdate(idx, 'label', e.target.value)}
                      placeholder={t('chat.promptLabel', 'Short name (e.g. 📋 Today summary)')}
                      className="w-full rounded-md border bg-background px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                      autoFocus
                    />
                    <textarea
                      value={item.prompt}
                      onChange={(e) => handleUpdate(idx, 'prompt', e.target.value)}
                      placeholder={t('chat.promptContent', 'Full question to ask AI...')}
                      rows={2}
                      className="w-full rounded-md border bg-background px-2 py-1.5 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={item.autoExecute}
                          onChange={(e) => handleUpdate(idx, 'autoExecute', e.target.checked)}
                          className="rounded"
                        />
                        <Zap className="h-3 w-3 text-primary" />
                        {t('chat.autoExecute', 'Send immediately on click')}
                      </label>
                      <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setEditIdx(null)}>
                        {t('common.done', 'Done')}
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setEditIdx(idx)}>
                      <p className="text-xs font-medium truncate">{item.label || '(untitled)'}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{item.prompt}</p>
                    </div>
                    {item.autoExecute && <Zap className="h-3 w-3 text-primary shrink-0" aria-label={t('chat.autoExecute', 'Send immediately on click')} />}
                    <button
                      className="p-1 rounded text-muted-foreground hover:text-destructive transition-colors shrink-0"
                      onClick={() => handleDelete(idx)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            ))}
            {items.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-6">
                {t('chat.noPrompts', 'No quick prompts. Click "Add" to create one.')}
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-3 border-t shrink-0">
            <Button size="sm" variant="outline" onClick={handleAdd}>
              <Plus className="h-3 w-3 mr-1" />
              {t('common.add', 'Add')}
            </Button>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setItems([...DEFAULT_QUICK_PROMPTS])}>
                {t('common.resetDefaults', 'Reset defaults')}
              </Button>
              <Button size="sm" onClick={handleSave}>
                {t('common.save', 'Save')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Main page ────────────────────────────────────

export default function ChatPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const mode = useRenderMode();
  const { convId: urlConvId } = usePageParams<{ convId?: string }>();
  const navigate = useNavigate();
  const pageContext = useChatContextStore((state) => state.pageContext);

  // Sessions
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(urlConvId ?? null);

  // Sync URL param → activeId when navigating to a specific conversation
  useEffect(() => {
    if (urlConvId && urlConvId !== activeId) setActiveId(urlConvId);
  }, [urlConvId]);

  // Mobile sidebar toggle
  const [showSidebar, setShowSidebar] = useState(false);
  const [localActivityMessages, setLocalActivityMessages] = useState<ChatMessage[]>([]);

  // Archive version counter — triggers re-render when archive state changes
  const [archiveVersion, setArchiveVersion] = useState(0);

  // HMS Job audit modal — opens when AI creates a job from an attachment
  const [auditAction, setAuditAction] = useState<ChatActionResult | null>(null);

  // Chat streaming
  const { messages, streaming, loading: msgsLoading, mode: chatMode, loadMessages, sendMessage, uploadFile, cancelStream } = useChat(activeId);
  // responseLanguage locked to 'auto' — AI auto-detects from user input
  const responseLanguage = 'auto' as const;

  // Quick prompts — customizable per user, persisted in localStorage
  const [quickPrompts, setQuickPrompts] = useState<QuickPrompt[]>(loadQuickPrompts);
  const [helperExpanded, setHelperExpanded] = useState(true);
  const [managePromptsOpen, setManagePromptsOpen] = useState(false);

  // Input state
  const [input, setInput] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const voiceCapability = useMemo(() => getVoiceInputCapability(), []);
  const liveTurnStartedAtRef = useRef<number | null>(null);
  const liveFirstPartialTrackedRef = useRef(false);
  const liveFirstOutputTrackedRef = useRef(false);
  const prevLiveStateRef = useRef<string>('idle');

  // ── Voice input (mic → STT) ──
  const voiceInput = useVoiceInput({
    onTranscription: (text) => {
      setInput(prev => prev ? `${prev} ${text}` : text);
      textareaRef.current?.focus();
    },
    onError: (msg) => toast.error(msg),
  });

  // ── Voice input enabled from user profile setting ──
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  useEffect(() => {
    apiFetch<{ voiceInputEnabled?: boolean }>('/auth/me/profile')
      .then((d) => setVoiceEnabled(d.voiceInputEnabled ?? false))
      .catch(() => { /* use default false */ });
  }, []);

  // ── Auto-focus input after AI reply finishes ──
  const prevStreamingRef = useRef(false);
  useEffect(() => {
    if (prevStreamingRef.current && !streaming) {
      textareaRef.current?.focus();
    }
    prevStreamingRef.current = streaming;
  }, [streaming]);

  // ── Text-to-Speech ──
  const tts = useTextToSpeech({
    role: 'multi_role',
    surface: 'web-client:chat-tts',
    profile: 'chat_assistant_readout',
    onError: (msg) => toast.error(msg),
  });
  const liveVoice = useGeminiLiveSession({
    role: 'multi_role',
    surface: 'web-client:chat-live-assistant',
    profile: 'chat_live_assistant',
    onInputTranscript: (text) => {
      if (!text.trim() || liveFirstPartialTrackedRef.current) return;
      liveFirstPartialTrackedRef.current = true;
      trackEvent('voice.live_session', 'first_partial_ms', {
        metadata: {
          surface: 'web-client:chat-live-assistant',
          transcriptLength: text.length,
          elapsedMs: getElapsedMs(liveTurnStartedAtRef.current),
        },
      });
    },
    onOutputTranscript: (text) => {
      if (!text.trim()) return;
      if (!liveFirstOutputTrackedRef.current) {
        liveFirstOutputTrackedRef.current = true;
        trackEvent('voice.live_session', 'remote_audio_start', {
          metadata: {
            surface: 'web-client:chat-live-assistant',
            transcriptLength: text.length,
            elapsedMs: getElapsedMs(liveTurnStartedAtRef.current),
          },
        });
      }
    },
    onError: (msg) => {
      trackEvent('voice.live_session', 'runtime_error', {
        outcome: 'error',
        metadata: {
          surface: 'web-client:chat-live-assistant',
          message: msg,
        },
      });
      toast.error(msg);
    },
  });
  const {
    session: liveSession,
    connected: liveConnected,
    state: liveSessionState,
    inputTranscript: liveInputTranscript,
    outputTranscript: liveOutputTranscript,
    connect: connectLiveVoice,
    disconnect: disconnectLiveVoice,
    startUserTurn: startLiveUserTurn,
    stopUserTurn: stopLiveUserTurn,
    interruptAssistant: interruptLiveAssistant,
  } = liveVoice;

  // ── Translate ──
  const translator = useTranslate();

  // ── Load sessions ──
  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const res = await api.get<{ items: ChatSession[] }>('/chat?limit=100');
      setSessions(res.items ?? []);
    } catch { /* ignore */ }
    finally { setSessionsLoading(false); }
  }, []);

  useEffect(() => { void loadSessions(); }, [loadSessions]);
  useEffect(() => { void loadMessages(); }, [loadMessages]);
  useEffect(() => {
    setLocalActivityMessages([]);
  }, [activeId]);

  useEffect(() => {
    const token = getToken();
    if (!token || !activeId) return;

    const url = `${BASE}/events/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);

    es.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as {
          type?: string;
          payload?: { conversationId?: string | null; status?: string };
        };

        if (message.type !== 'approval.reviewed') return;
        if (message.payload?.conversationId !== activeId) return;

        void loadMessages();
      } catch {
        // ignore malformed SSE events
      }
    };

    return () => es.close();
  }, [activeId, loadMessages]);

  // ── Listen for boox:chat-prompt events from other pages ──
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{
        prompt: string;
        autoResolveAction?: boolean;
        payload?: Record<string, unknown>;
      }>).detail;
      const prompt = detail?.prompt;
      if (prompt) {
        setInput(prompt);
        textareaRef.current?.focus();
      }
      if (prompt && detail?.autoResolveAction) {
        void api.post<{
          status: string;
          widget?: ChatWidget;
          message?: string;
          actionId?: string;
          missingFields?: string[];
        }>('/ai-actions/resolve', {
          message: prompt,
          surface: 'ai-cos',
          context: {
            pageKey: pageContext.pageKey,
            entityType: pageContext.entityType,
            entityId: pageContext.entityId,
          },
          payload: detail.payload ?? {},
        }).then((result) => {
          setLocalActivityMessages((prev) => [
            ...prev,
            {
              id: `local-action-${Date.now()}-${prev.length}`,
              role: 'assistant',
              content: result.widget ? '' : (result.message ?? 'No governed action matched this conversation turn yet.'),
              widgetData: result.widget,
              thinkingSteps: [
                { type: 'thinking', text: 'Read owner intent and current builder context.', timestamp: new Date().toISOString() },
                { type: 'tool_call', text: `Resolve governed action${result.actionId ? `: ${result.actionId}` : ''}.`, toolName: 'ai-actions.resolve', timestamp: new Date().toISOString() },
                { type: 'tool_result', text: `Status: ${result.status}${result.missingFields?.length ? `, missing: ${result.missingFields.join(', ')}` : ''}.`, toolName: 'ai-actions.resolve', timestamp: new Date().toISOString() },
              ],
              createdAt: new Date().toISOString(),
            },
          ]);
        }).catch((error) => {
          setLocalActivityMessages((prev) => [
            ...prev,
            {
              id: `local-action-error-${Date.now()}-${prev.length}`,
              role: 'assistant',
              content: `Action resolve failed: ${error instanceof Error ? error.message : 'unknown error'}`,
              createdAt: new Date().toISOString(),
            },
          ]);
        });
      }
    };
    window.addEventListener('boox:chat-prompt', handler);
    return () => window.removeEventListener('boox:chat-prompt', handler);
  }, [pageContext.entityId, pageContext.entityType, pageContext.pageKey]);

  useEffect(() => {
    const handler = (e: Event) => {
      const action = (e as CustomEvent<ChatActionResult>).detail;
      if (!action) return;

      // For HMS job actions with an attachment — open the audit modal
      if (
        (action.actionType === 'create_hms_import_job_from_attachment' ||
         action.actionType === 'create_hms_import_job') &&
        (action.attachmentKey || action.status === 'needs_confirmation' || action.status === 'created')
      ) {
        setAuditAction(action);
        return;
      }

      // Fallback: plain navigation
      if (!action.navigationHint?.path) return;
      navigate(action.navigationHint.path);
      if (action.status === 'created' && action.jobNumber) {
        toast.success(`已创建 job ${action.jobNumber}`);
      }
      if (action.status === 'needs_confirmation' && action.missingFields?.length) {
        toast.error(`无法自动建单，缺少字段：${action.missingFields.join('、')}`);
      }
    };
    window.addEventListener('boox:chat-action', handler);
    return () => window.removeEventListener('boox:chat-action', handler);
  }, [navigate, toast]);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Create new session ──
  const handleNewSession = async () => {
    try {
      const res = await api.post<ChatSession>('/chat', {});
      setSessions(prev => [res, ...prev]);
      setActiveId(res.id);
      setHelperExpanded(true);
      if (mode === 'mobile') setShowSidebar(false);
    } catch {
      toast.error('Failed to create conversation');
    }
  };

  // ── Delete session ──
  const handleDeleteSession = async (id: string) => {
    const session = sessions.find(s => s.id === id);
    const label = session?.subject ?? 'this conversation';
    if (!window.confirm(t('chat.confirmDelete', `确定要删除「${label}」吗？`))) return;
    try {
      await api.delete(`/chat/${id}`);
      setSessions(prev => prev.filter(s => s.id !== id));
      if (activeId === id) setActiveId(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`${t('chat.deleteFailed', '删除对话失败')}: ${msg}`);
    }
  };

  // ── Send message ──
  const handleSend = async (directPrompt?: string) => {
    const content = (directPrompt ?? input).trim();
    if (!content && pendingFiles.length === 0) return;
    let targetConvId = activeId;
    let isNewSession = false;
    if (!targetConvId) {
      // Auto-create session
      try {
        const res = await api.post<ChatSession>('/chat', { title: content.slice(0, 60) });
        setSessions(prev => [res, ...prev]);
        targetConvId = res.id;
        isNewSession = true;
      } catch {
        toast.error('Failed to create conversation');
        return;
      }
    }

    setInput('');
    setPendingFiles([]);

    // Upload attachments first
    let attachmentKeys: string[] = [];
    if (pendingFiles.length > 0) {
      setUploading(true);
      try {
        attachmentKeys = await Promise.all(pendingFiles.map(uploadFile));
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Upload failed');
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    trackEvent('ai.chat', 'query', { metadata: { conv_id: targetConvId, has_files: attachmentKeys.length > 0 } });
    let assistantText: string | undefined;
    try {
      assistantText = await sendMessage(content || '(files attached)', {
        attachmentKeys,
        targetConvId,
        responseLanguage,
      });
    } catch {
      // sendMessage handles its own error display
    }

    // Set activeId AFTER streaming is done to avoid loadMessages race condition
    if (isNewSession) {
      setActiveId(targetConvId);
    }

    return assistantText;
  };

  useEffect(() => {
    if (prevLiveStateRef.current === 'speaking' && liveSessionState === 'ready') {
      trackEvent('voice.live_session', 'remote_audio_end', {
        metadata: {
          surface: 'web-client:chat-live-assistant',
        },
      });
    }
    prevLiveStateRef.current = liveSessionState;
  }, [liveSessionState]);

  const handleToggleLiveSession = useCallback(async () => {
    if (liveConnected) {
      disconnectLiveVoice();
      liveTurnStartedAtRef.current = null;
      trackEvent('voice.live_session', 'disconnect', {
        metadata: {
          surface: 'web-client:chat-live-assistant',
          provider: liveSession?.provider ?? null,
          transport: liveSession?.transport ?? null,
        },
      });
      return;
    }

    try {
      const sessionCreateStartedAt = getNowMs();
      const nextSession = await connectLiveVoice();
      trackEvent('voice.live_session', 'create', {
        metadata: {
          surface: 'web-client:chat-live-assistant',
          capability: voiceCapability.level,
          supportsRealtime: voiceCapability.supportsRealtime,
          provider: nextSession.provider,
          transport: nextSession.transport,
          createMs: getElapsedMs(sessionCreateStartedAt),
        },
      });
      toast.success('Live assistant ready');
    } catch (error) {
      trackEvent('voice.live_session', 'create_fail', {
        outcome: 'error',
        metadata: {
          surface: 'web-client:chat-live-assistant',
          capability: voiceCapability.level,
          message: error instanceof Error ? error.message : 'Failed to create voice session',
        },
      });
      // hook already surfaced error
    }
  }, [connectLiveVoice, disconnectLiveVoice, liveConnected, liveSession?.provider, liveSession?.transport, toast, voiceCapability.level, voiceCapability.supportsRealtime]);

  const handleLiveTurn = useCallback(async () => {
    if (!voiceCapability.supportsRealtime) {
      toast.error('Live assistant requires realtime browser voice support.');
      return;
    }

    if (!liveConnected) {
      try {
        const sessionCreateStartedAt = getNowMs();
        const nextSession = await connectLiveVoice();
        trackEvent('voice.live_session', 'create', {
          metadata: {
            surface: 'web-client:chat-live-assistant',
            capability: voiceCapability.level,
            supportsRealtime: voiceCapability.supportsRealtime,
            provider: nextSession.provider,
            transport: nextSession.transport,
            createMs: getElapsedMs(sessionCreateStartedAt),
          },
        });
      } catch (error) {
        trackEvent('voice.live_session', 'create_fail', {
          outcome: 'error',
          metadata: {
            surface: 'web-client:chat-live-assistant',
            capability: voiceCapability.level,
            message: error instanceof Error ? error.message : 'Failed to create voice session',
          },
        });
        return;
      }
    }

    if (liveSessionState === 'speaking') {
      trackEvent('voice.live_session', 'interrupt_request', {
        metadata: {
          surface: 'web-client:chat-live-assistant',
          provider: liveSession?.provider ?? null,
          transport: liveSession?.transport ?? null,
        },
      });
      interruptLiveAssistant();
      trackEvent('voice.live_session', 'barge_in', {
        metadata: {
          surface: 'web-client:chat-live-assistant',
          provider: liveSession?.provider ?? null,
          transport: liveSession?.transport ?? null,
        },
      });
      await startLiveUserTurn();
      return;
    }

    if (liveSessionState === 'listening') {
      stopLiveUserTurn();
      trackEvent('voice.live_chat', 'stop_turn', {
        metadata: {
          surface: 'web-client:chat-live-assistant',
        },
      });
      return;
    }

    if (streaming || voiceInput.recording || voiceInput.transcribing) return;

    liveTurnStartedAtRef.current = getNowMs();
    liveFirstPartialTrackedRef.current = false;
    liveFirstOutputTrackedRef.current = false;
    trackEvent('voice.live_chat', 'start_turn');
    await startLiveUserTurn();
  }, [connectLiveVoice, interruptLiveAssistant, liveConnected, liveSession?.provider, liveSession?.transport, liveSessionState, startLiveUserTurn, stopLiveUserTurn, streaming, toast, voiceCapability.level, voiceCapability.supportsRealtime, voiceInput.recording, voiceInput.transcribing]);

  // ── Widget action handler — routes all actions through unified action bus ──
  const handleWidgetAction = async (type: string, payload: unknown) => {
    const data = payload as Record<string, unknown>;

    if (type === 'request_approval') {
      const urgency = String(data.urgency ?? 'medium');
      const priority = urgency === 'high' ? 'P0' : urgency === 'medium' ? 'P1' : 'P2';
      const timeoutMinutes = urgency === 'high' ? 15 : urgency === 'medium' ? 60 : 240;
      const title = String(data.title ?? 'Approval required');
      const description = String(data.description ?? 'Human review is required before execution.');
      const reviewerRole = String(data.reviewerRole ?? 'manager');
      const requesterRole = String(data.requesterRole ?? 'chat_user');
      const escalationRole =
        typeof data.escalationRole === 'string' && data.escalationRole.trim().length > 0
          ? data.escalationRole
          : undefined;
      const requestPayload =
        data.payload && typeof data.payload === 'object'
          ? data.payload as Record<string, unknown>
          : {};

      const approval = await api.post<{ id: string; status: string; title: string }>(
        '/approvals',
        {
          requestType: 'custom',
          requesterRole,
          reviewerRole,
          title,
          description,
          payload: requestPayload,
          priority,
          timeoutMinutes,
          ...(escalationRole ? { escalationRole } : {}),
        },
      );

      void sendMessage(
        `[ACTION_EXECUTED:request_approval] ${JSON.stringify({
          approvalId: approval.id,
          title,
          reviewerRole,
          priority,
        })}`,
      );
      return approval;
    }

    if (type === 'action_form_submit') {
      const actionId = typeof data.actionId === 'string' ? data.actionId : '';
      const executionMode = typeof data.executionMode === 'string' ? data.executionMode : 'dry-run';
      const values = data.values && typeof data.values === 'object' && !Array.isArray(data.values)
        ? data.values as Record<string, unknown>
        : {};
      if (!actionId) {
        toast.error('Action form is missing actionId.');
        return;
      }
      try {
        const result = await api.post<Record<string, unknown>>('/ai-actions/dispatch', {
          actionId,
          executionMode,
          payload: values,
          idempotencyKey: `chat-${actionId}-${String(data.widgetId ?? 'form')}-${Math.floor(Date.now() / 10000)}`,
          metadata: {
            source: 'chat.action_form',
            widgetId: data.widgetId,
            schemaVersion: data.schemaVersion,
          },
        });
        toast.success('Action preview ready');
        if (actionId === 'business-builder.guided-branch') {
          const actionResult = result as {
            status?: string;
            message?: string;
            sideEffectPolicy?: string;
            dryRun?: {
              branchName?: unknown;
              countryCode?: unknown;
              finalPublishLocked?: unknown;
              guidedBuildPack?: {
                builderSummary?: string;
                draftComponents?: Array<{ label?: string; componentType?: string; status?: string }>;
              };
            };
            approval?: { required?: boolean; status?: string; approvalId?: string };
            audit?: { auditId?: string; traceable?: boolean; stored?: boolean };
          };
          const draftComponents = actionResult.dryRun?.guidedBuildPack?.draftComponents ?? [];
          setLocalActivityMessages((prev) => [
            ...prev,
            {
              id: `local-builder-action-${Date.now()}-${prev.length}`,
              role: 'assistant',
              content: [
                `Business Builder action result: ${actionResult.status ?? 'unknown'}.`,
                actionResult.dryRun?.guidedBuildPack?.builderSummary ?? actionResult.message ?? '',
                `Branch: ${String(actionResult.dryRun?.branchName ?? values.branchName ?? 'pending')}`,
                `Side effect policy: ${actionResult.sideEffectPolicy ?? 'contract-only'}. Final publish locked: ${String(actionResult.dryRun?.finalPublishLocked ?? true)}.`,
                `Approval: ${actionResult.approval?.required ? actionResult.approval.status ?? 'required' : 'not required'}. Audit: ${actionResult.audit?.auditId ?? 'trace pending'}.`,
                draftComponents.length > 0 ? `Draft parts: ${draftComponents.map((component) => component.label ?? component.componentType).join(', ')}.` : '',
              ].filter(Boolean).join('\n\n'),
              thinkingSteps: [
                { type: 'tool_call', text: 'Dispatch submitted through governed AI action bus.', toolName: 'ai-actions.dispatch', timestamp: new Date().toISOString() },
                { type: 'tool_result', text: `Dispatch status: ${actionResult.status ?? 'unknown'}; side effect policy: ${actionResult.sideEffectPolicy ?? 'unknown'}.`, toolName: 'ai-actions.dispatch', timestamp: new Date().toISOString() },
              ],
              createdAt: new Date().toISOString(),
            },
          ]);
        }
        void sendMessage(
          `[ACTION_DISPATCHED:${actionId}] ${JSON.stringify(result)}`,
        );
        return result;
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to dispatch action form');
        void sendMessage(
          `[ACTION_DISPATCH_FAILED:${actionId}] ${JSON.stringify({ error: err instanceof Error ? err.message : 'dispatch_failed' })}`,
        );
        return;
      }
    }

    const entityId = String(data.requestId ?? data.widgetId ?? data.entityId ?? '');
    const entityType = String(data.entityType ?? (type === 'approval' ? 'approval' : 'shipment'));

    // Map widget action types to action bus actionType
    // Approval requires explicit decision — never default to 'approve' on ambiguous input
    let actionType: string | undefined;
    if (type === 'approval') {
      const decision = String(data.decision ?? '');
      if (decision === 'approved') actionType = 'approve';
      else if (decision === 'rejected') actionType = 'reject';
      // else: ambiguous decision → don't execute, only send as chat message
    } else {
      const actionTypeMap: Record<string, string> = {
        resend: 'resend',
        forward: 'forward',
        'follow-up': 'follow-up',
        close: 'close',
        release: 'release',
        acknowledge: 'acknowledge',
      };
      actionType = actionTypeMap[type];
    }

    if (actionType && entityId) {
      // Idempotency strategy:
      // - One-time actions (approve/reject/release/close/acknowledge): use permanent stable key
      //   so retries/double-clicks are always deduplicated
      // - Repeatable actions (resend/forward/follow-up): use 10s time-window key
      //   so rapid clicks are deduplicated, but genuinely new actions minutes later get a new key
      const oneTimeActions = new Set(['approve', 'reject', 'release', 'close', 'acknowledge']);
      const stableRequestId = oneTimeActions.has(actionType)
        ? `chat-${actionType}-${entityId}`
        : `chat-${actionType}-${entityId}-${Math.floor(Date.now() / 10000)}`;
      try {
        await api.post('/actions/execute', {
          requestId: stableRequestId,
          actionType,
          entityType,
          entityId,
          performedBy: 'chat-user',
          reason: `Via chat widget`,
          payload: data,
        });
      } catch {
        // If action bus call fails, fall through to send as chat message
      }
    }

    // Notify AI of the executed action — prefixed to prevent AI from re-executing
    void sendMessage(
      `[ACTION_EXECUTED:${type}] ${JSON.stringify(payload)}`,
    );
  };

  const appendLocalActivity = useCallback((content: string) => {
    setLocalActivityMessages((prev) => [
      ...prev,
      {
        id: `local-${Date.now()}-${prev.length}`,
        role: 'system',
        content,
        createdAt: new Date().toISOString(),
      },
    ]);
  }, []);

  const workspaceActions = useMemo<WorkspaceAction[]>(() => {
    if (pageContext.pageKey === 'packages.manager' && pageContext.entityType === 'business_package_draft' && pageContext.entityId) {
      return [
        { id: 'draft-preflight', label: 'Run Preflight' },
        { id: 'draft-simulate', label: 'Run Simulation' },
        { id: 'draft-install', label: 'Install Draft', tone: 'warn' },
      ];
    }
    if (pageContext.pageKey === 'onboarding.records' && pageContext.entityType === 'onboarding_record' && pageContext.entityId) {
      return [
        { id: 'record-delivery-review', label: 'Create Delivery Review' },
        { id: 'record-sales-followup', label: 'Create Sales Follow-up' },
        { id: 'record-support-review', label: 'Create Support Review' },
      ];
    }
    if (pageContext.pageKey === 'onboarding.control') {
      return [
        { id: 'onboarding-orchestration', label: 'Run Lifecycle Orchestration' },
      ];
    }
    if (pageContext.pageKey === 'billing.settings') {
      return [
        { id: 'billing-maintenance', label: 'Run Billing Automation' },
        { id: 'billing-settlement', label: 'Run Monthly Settlement', tone: 'warn' },
        { id: 'billing-grant', label: 'Grant Starter' },
        { id: 'billing-topup', label: 'Top-up 120' },
      ];
    }
    return [];
  }, [pageContext.entityId, pageContext.entityType, pageContext.pageKey]);

  const [workspaceActionBusy, setWorkspaceActionBusy] = useState<string | null>(null);

  const handleWorkspaceAction = useCallback(async (actionId: string) => {
    setWorkspaceActionBusy(actionId);
    const writeWorkspaceOutcome = async (summary: string) => {
      appendLocalActivity(summary);
      if (pageContext.entityType && pageContext.entityId) {
        await postEntityThreadNote({
          entityType: pageContext.entityType,
          entityId: pageContext.entityId,
          content: `[Workspace Action] ${summary}`,
        }).catch(() => undefined);
      }
    };
    try {
      if (actionId === 'draft-preflight' && pageContext.entityId) {
        await api.post(`/composer/drafts/${pageContext.entityId}/preflight`, {});
        toast.success('Draft preflight completed');
        await writeWorkspaceOutcome('Preflight 已完成。你现在可以继续查看 blocking items，或直接运行 simulation。');
      } else if (actionId === 'draft-simulate' && pageContext.entityId) {
        await api.post(`/composer/drafts/${pageContext.entityId}/simulate`, { mode: 'schema_and_surface' });
        toast.success('Draft simulation completed');
        await writeWorkspaceOutcome('Simulation 已完成。建议继续检查 scenario evidence、approval checkpoints 和 risk findings。');
      } else if (actionId === 'draft-install' && pageContext.entityId) {
        await api.post(`/composer/drafts/${pageContext.entityId}/install`, {});
        toast.success('Draft install completed');
        await writeWorkspaceOutcome('Draft install 已完成。下一步建议检查 install runtime status，必要时再走 validate 或 share to Exchange。');
      } else if (actionId === 'record-delivery-review' && pageContext.entityId) {
        await api.post('/autonomous-tasks', {
          runId: `chat-delivery-${Date.now()}`,
          skillId: 'onboarding.concierge',
          taskType: 'delivery_review',
          title: 'Delivery review from chat',
          summary: 'Created from workspace-native chat action.',
          channel: 'manual',
          domain: 'gtm',
          entityType: 'onboarding-record',
          entityId: pageContext.entityId,
          scheduleMode: 'immediate',
          waitReason: 'needs_human_followup',
          actionRoute: '/admin/onboarding-records',
          resultRoute: '/admin/tasks',
          status: 'awaiting_confirmation',
          metadata: {
            source: 'chat-workspace-action',
            onboardingRecordId: pageContext.entityId,
          },
        });
        toast.success('Delivery review task created');
        await writeWorkspaceOutcome('已创建 Delivery Review 任务，并挂到当前 onboarding record。');
      } else if (actionId === 'record-sales-followup' && pageContext.entityId) {
        await api.post('/autonomous-tasks', {
          runId: `chat-sales-${Date.now()}`,
          skillId: 'onboarding.concierge',
          taskType: 'sales_followup',
          title: 'Sales follow-up from chat',
          summary: 'Created from workspace-native chat action.',
          channel: 'manual',
          domain: 'gtm',
          entityType: 'onboarding-record',
          entityId: pageContext.entityId,
          scheduleMode: 'immediate',
          waitReason: 'needs_human_followup',
          actionRoute: '/admin/onboarding-records',
          resultRoute: '/admin/tasks',
          status: 'awaiting_confirmation',
          metadata: {
            source: 'chat-workspace-action',
            onboardingRecordId: pageContext.entityId,
          },
        });
        toast.success('Sales follow-up task created');
        await writeWorkspaceOutcome('已创建 Sales Follow-up 任务，可继续在 Task Center 追踪商业跟进。');
      } else if (actionId === 'record-support-review' && pageContext.entityId) {
        await api.post('/autonomous-tasks', {
          runId: `chat-support-${Date.now()}`,
          skillId: 'onboarding.concierge',
          taskType: 'support_review',
          title: 'Support review from chat',
          summary: 'Created from workspace-native chat action.',
          channel: 'manual',
          domain: 'gtm',
          entityType: 'onboarding-record',
          entityId: pageContext.entityId,
          scheduleMode: 'immediate',
          waitReason: 'needs_human_followup',
          actionRoute: '/admin/onboarding-records',
          resultRoute: '/admin/tasks',
          status: 'awaiting_confirmation',
          metadata: {
            source: 'chat-workspace-action',
            onboardingRecordId: pageContext.entityId,
          },
        });
        toast.success('Support review task created');
        await writeWorkspaceOutcome('已创建 Support Review 任务，用于 hypercare / early-support 跟进。');
      } else if (actionId === 'onboarding-orchestration') {
        const result = await api.post<{ createdCount?: number; meteredCredits?: number }>('/onboarding/lifecycle-orchestration/run', {
          includeBriefs: true,
          includeEscalations: true,
          limit: 12,
        });
        toast.success('Lifecycle orchestration completed');
        await writeWorkspaceOutcome(`Lifecycle orchestration 已运行，新增 ${result.createdCount ?? 0} 个任务，计量 ${result.meteredCredits ?? 0} credits。`);
      } else if (actionId === 'billing-maintenance') {
        await api.post('/billing/automation/run-maintenance', { includePreviousMonth: true });
        toast.success('Billing automation completed');
        await writeWorkspaceOutcome('Billing automation 已完成。当前可继续检查 ledger、usage summary 和 low-credit risk。');
      } else if (actionId === 'billing-settlement') {
        await api.post('/billing/credit-ledger/run-monthly-settlement', {});
        toast.success('Monthly settlement completed');
        await writeWorkspaceOutcome('Monthly settlement 已完成。starter baseline 和 opening balance 已刷新。');
      } else if (actionId === 'billing-grant') {
        await api.post('/billing/credit-ledger/grant-starter', {});
        toast.success('Starter grant completed');
        await writeWorkspaceOutcome('Starter grant 已发放。建议回到 billing summary 检查余额和 metered actions。');
      } else if (actionId === 'billing-topup') {
        await api.post('/billing/credit-ledger/top-up', { credits: 120, notes: 'Top-up from chat workspace action' });
        toast.success('Top-up completed');
        await writeWorkspaceOutcome('Top-up 120 已完成。余额和 ledger 已更新。');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Workspace action failed');
      appendLocalActivity(`Workspace action 失败：${error instanceof Error ? error.message : 'unknown error'}`);
    } finally {
      setWorkspaceActionBusy(null);
    }
  }, [appendLocalActivity, pageContext.entityId, toast]);

  const renderedMessages = useMemo(
    () =>
      [...messages, ...localActivityMessages].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      ),
    [localActivityMessages, messages],
  );

  // ── Share chat ──
  const [shareOpen, setShareOpen] = useState(false);
  const [shareExpiry, setShareExpiry] = useState('15m');
  const [shareUrl, setShareUrl] = useState('');
  const [shareExpiresAt, setShareExpiresAt] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [excludedPairs, setExcludedPairs] = useState<Set<number>>(new Set());

  // Group messages into Q+A pairs (user question + assistant answer)
  const qaPairs = (() => {
    const pairs: { index: number; user: ChatMessage; assistant?: ChatMessage }[] = [];
    for (let i = 0; i < messages.length; i++) {
      const m = messages[i];
      if (!m || m.role !== 'user') continue;
      const next = messages[i + 1];
      pairs.push({
        index: pairs.length,
        user: m,
        assistant: next?.role === 'assistant' ? next : undefined,
      });
      if (next?.role === 'assistant') i++; // skip the paired answer
    }
    return pairs;
  })();

  const togglePair = (idx: number) => {
    setExcludedPairs(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
    setShareUrl(''); // reset link when selection changes
  };

  const handleShare = async () => {
    if (!activeId) return;
    setShareLoading(true);
    setShareUrl('');
    try {
      // Collect message IDs to include (exclude deselected pairs)
      const includedIds: string[] = [];
      for (const pair of qaPairs) {
        if (!excludedPairs.has(pair.index)) {
          includedIds.push(pair.user.id);
          if (pair.assistant) includedIds.push(pair.assistant.id);
        }
      }

      const res = await api.post<{ token: string; shareUrl: string; expiresAt: string; expiresIn: string }>(
        `/chat/${activeId}/share`,
        {
          expiresIn: shareExpiry,
          ...(excludedPairs.size > 0 ? { messageIds: includedIds } : {}),
        },
      );
      const fullUrl = `https://share.boox.app/c/${res.token}`;
      setShareUrl(fullUrl);
      setShareExpiresAt(res.expiresAt);
    } catch {
      toast.error('Failed to create share link');
    } finally {
      setShareLoading(false);
    }
  };

  const handleCopyShareUrl = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  // ── Keyboard shortcut ──
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  // ── Auto-resize textarea ──
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  };

  // ─── Render ───────────────────────────────────────

  // ── Archive session (hide from active list, keep for future retrieval) ──
  const handleArchiveSession = async (id: string) => {
    try {
      const archived: string[] = JSON.parse(localStorage.getItem(ARCHIVED_SESSIONS_KEY) ?? '[]');
      if (!archived.includes(id)) {
        archived.push(id);
        localStorage.setItem(ARCHIVED_SESSIONS_KEY, JSON.stringify(archived));
      }
      // Don't remove from sessions — just let the filter hide it
      if (activeId === id) setActiveId(null);
      // Force re-render by updating a counter
      setArchiveVersion(v => v + 1);
      toast.success(t('chat.archived', 'Conversation archived'));
    } catch {
      toast.error('Failed to archive');
    }
  };

  const handleRestoreSession = (id: string) => {
    try {
      const archived: string[] = JSON.parse(localStorage.getItem(ARCHIVED_SESSIONS_KEY) ?? '[]');
      localStorage.setItem(ARCHIVED_SESSIONS_KEY, JSON.stringify(archived.filter(a => a !== id)));
      setArchiveVersion(v => v + 1);
      toast.success(t('chat.restored', 'Conversation restored'));
    } catch {
      toast.error('Failed to restore');
    }
  };

  // Filter out archived sessions from display
  const archivedIds = (() => {
    try { return new Set<string>(JSON.parse(localStorage.getItem(ARCHIVED_SESSIONS_KEY) ?? '[]')); }
    catch { return new Set<string>(); }
  })();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _archiveVer = archiveVersion; // read to trigger re-render
  const visibleSessions = sessions.filter(s => !archivedIds.has(s.id));
  const archivedSessions = sessions.filter(s => archivedIds.has(s.id));

  const sidebarContent = (
    <SessionList
      sessions={visibleSessions}
      activeId={activeId}
      onSelect={id => { setActiveId(id); setShowSidebar(false); }}
      onNew={() => void handleNewSession()}
      onDelete={id => void handleDeleteSession(id)}
      onArchive={id => void handleArchiveSession(id)}
      onRestore={id => void handleRestoreSession(id)}
      archivedSessions={archivedSessions}
      loading={sessionsLoading}
    />
  );

  const chatContent = (
    <div className="flex flex-col flex-1 min-h-0 relative">
      {/* Header */}
      <div className="flex items-center gap-2 border-b px-4 py-2.5 shrink-0">
        {mode === 'mobile' && (
          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => setShowSidebar(true)}>
            <Menu className="h-4 w-4" />
          </Button>
        )}
        <Bot className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium truncate flex-1">
          {activeId
            ? (sessions.find(s => s.id === activeId)?.subject ?? t('chat.chatTitle', 'Chat'))
            : t('chat.newChat', 'New conversation')
          }
        </span>
        {streaming && (
          <Badge variant="outline" className="text-[10px] gap-1">
            <Loader2 className="h-2.5 w-2.5 animate-spin" />
            {t('chat.thinking', 'Thinking…')}
          </Badge>
        )}
        {chatMode && (
          <Badge
            variant="outline"
            className={cn(
              'text-[10px] gap-1',
              chatMode === 'brain'
                ? 'border-green-400 text-green-700 dark:text-green-400'
                : 'border-yellow-400 text-yellow-700 dark:text-yellow-400',
            )}
          >
            <span className={cn(
              'inline-block h-1.5 w-1.5 rounded-full',
              chatMode === 'brain' ? 'bg-green-500' : 'bg-yellow-500',
            )} />
            {chatMode === 'brain' ? 'Brain' : 'Basic'}
          </Badge>
        )}
        {/* Share button */}
        {activeId && messages.length > 0 && (
          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0"
            onClick={() => { setShareOpen(true); setShareUrl(''); setShareCopied(false); setExcludedPairs(new Set()); }}
            title={t('chat.share', 'Share conversation')}>
            <Share2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {workspaceActions.length > 0 && (
        <div className="border-b px-4 py-2 shrink-0">
          <div className="mb-1 text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Workspace Actions
          </div>
          <div className="flex flex-wrap gap-1.5">
            {workspaceActions.map((action) => (
              <button
                key={action.id}
                type="button"
                onClick={() => void handleWorkspaceAction(action.id)}
                disabled={workspaceActionBusy !== null}
                className={cn(
                  'rounded-full border px-2.5 py-1 text-[11px] transition-colors disabled:opacity-50',
                  action.tone === 'warn'
                    ? 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100'
                    : 'border-border bg-muted/40 hover:bg-primary/10 hover:border-primary/30 hover:text-primary',
                )}
              >
                {workspaceActionBusy === action.id ? `Running ${action.label}…` : action.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {msgsLoading && (
          <div className="flex justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}
        {!msgsLoading && !activeId && (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground py-20">
            <Bot className="h-10 w-10 opacity-20" />
            <p className="text-sm">{t('chat.typeToStart', 'Type below to start a conversation')}</p>
          </div>
        )}
        {!msgsLoading && activeId && messages.length === 0 && !streaming && (
          <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground py-20">
            <MessageSquare className="h-8 w-8 opacity-20" />
            <p className="text-sm">{t('chat.startTyping', 'Say something to start…')}</p>
          </div>
        )}
        {renderedMessages.map((msg, i) => (
          <MessageBubble
            key={msg.id}
            msg={msg}
            streaming={streaming && i === renderedMessages.length - 1 && msg.role === 'assistant'}
            conversationId={activeId}
            onAction={handleWidgetAction}
            onSpeak={(text, id) => tts.speak(text, id)}
            onStopSpeak={tts.stop}
            isSpeaking={tts.speakingId === msg.id && tts.speaking}
            isSpeakLoading={tts.speakingId === msg.id && tts.loading}
            translation={translator.translations.get(msg.id)}
            onTranslate={translator.translate}
            isTranslating={translator.translating === msg.id}
            onClearTranslation={translator.clearTranslation}
          />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Pending file chips */}
      {pendingFiles.length > 0 && (
        <div className="border-t px-3 py-2 flex flex-wrap gap-1.5">
          {pendingFiles.map((f, i) => (
            <Badge key={i} variant="secondary" className="gap-1 pr-1">
              <Paperclip className="h-2.5 w-2.5" />
              <span className="max-w-[100px] truncate text-[10px]">{f.name}</span>
              <button onClick={() => setPendingFiles(prev => prev.filter((_, j) => j !== i))}>
                <X className="h-3 w-3 ml-0.5" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Interim transcript shown while recording (Web Speech API real-time) */}
      {voiceEnabled && voiceInput.recording && (voiceInput.interimTranscript || voiceInput.transcript) && (
        <div className="border-t px-3 py-1.5 bg-red-50 dark:bg-red-950/20">
          <p className="text-xs text-muted-foreground">
            {voiceInput.transcript && <span className="text-foreground">{voiceInput.transcript}</span>}
            {voiceInput.interimTranscript && (
              <span className="text-muted-foreground/60 italic">{voiceInput.interimTranscript}</span>
            )}
          </p>
        </div>
      )}

      {liveConnected && (
        <div className="border-t px-3 py-1.5 bg-sky-50 dark:bg-sky-950/20">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <p className="text-[11px] font-medium text-sky-700 dark:text-sky-300">
                Live Assistant Beta
              </p>
              <span className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-sky-700 dark:bg-sky-900/50 dark:text-sky-200">
                {liveSession?.provider === 'gemini_live' ? 'Gemini Live' : liveSession?.provider ?? 'Live'}
              </span>
            </div>
            <span className="text-[10px] uppercase tracking-wide text-sky-700/70 dark:text-sky-300/70">
              {liveSessionState}
            </span>
          </div>
          {liveSession && (
            <p className="mt-1 text-[10px] text-sky-700/70 dark:text-sky-300/70">
              {liveSession.transport} · {liveSession.model}
            </p>
          )}
          {liveInputTranscript && (
            <p className="mt-1 text-xs text-muted-foreground">
              You: {liveInputTranscript}
            </p>
          )}
          {liveOutputTranscript && (
            <p className="mt-1 text-xs text-sky-800 dark:text-sky-200">
              Gemini: {liveOutputTranscript}
            </p>
          )}
        </div>
      )}

      {/* Input bar */}
      <div className="border-t px-3 py-2.5 shrink-0" style={{ paddingBottom: 'max(0.625rem, env(safe-area-inset-bottom))' }}>
        {/* Quick-prompt helper area — always visible, with collapse toggle */}
        {quickPrompts.length > 0 && (
          <div className="mb-1.5">
            <div className="flex items-center gap-1 mb-1">
              <button
                className="flex items-center gap-0.5 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setHelperExpanded(!helperExpanded)}
              >
                {helperExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
                <span className="font-medium">{t('chat.quickPrompts', 'Quick Prompts')}</span>
              </button>
              {helperExpanded && (
                <button
                  className="ml-auto p-0.5 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setManagePromptsOpen(true)}
                  title={t('chat.managePrompts', 'Manage prompts')}
                >
                  <Settings2 className="h-3 w-3" />
                </button>
              )}
            </div>
            {helperExpanded && (
              <div className="flex flex-wrap gap-1.5">
                {quickPrompts.map(qp => (
                  <button
                    key={qp.id}
                    onClick={() => {
                      if (qp.autoExecute) {
                        void handleSend(qp.prompt);
                      } else {
                        setInput(qp.prompt);
                        textareaRef.current?.focus();
                      }
                    }}
                    title={qp.prompt}
                    className={cn(
                      'text-[11px] px-2.5 py-1 rounded-full border transition-colors whitespace-nowrap flex items-center gap-0.5',
                      qp.autoExecute
                        ? 'border-primary/30 bg-primary/5 hover:bg-primary/15 hover:text-primary'
                        : 'border-border bg-muted/40 hover:bg-primary/10 hover:border-primary/30 hover:text-primary',
                    )}
                  >
                    {qp.autoExecute && <Zap className="h-2.5 w-2.5" />}
                    {qp.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="flex items-end gap-2 rounded-xl border bg-background p-2 focus-within:ring-1 focus-within:ring-primary">
          {/* Attach button */}
          <button
            className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors shrink-0"
            title="Attach files"
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
          </button>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            accept="image/*,.pdf,application/pdf"
            onChange={e => {
              const files = Array.from(e.target.files ?? []);
              setPendingFiles(prev => [...prev, ...files]);
              e.target.value = '';
            }}
          />

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={t('chat.inputPlaceholder', 'Ask anything… (Enter to send, Shift+Enter for newline)')}
            rows={1}
            className="flex-1 resize-none bg-transparent text-sm outline-none placeholder:text-muted-foreground leading-relaxed max-h-40 overflow-y-auto"
          />

          {/* Mic button — tap-to-toggle voice input */}
          {voiceEnabled && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              className={cn(
                'p-1.5 rounded-lg transition-colors',
                liveConnected
                  ? 'bg-sky-100 text-sky-700 hover:bg-sky-200'
                  : voiceCapability.supportsRealtime
                    ? 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    : 'text-muted-foreground/50 cursor-not-allowed',
              )}
              onClick={() => { void handleToggleLiveSession(); }}
              disabled={!voiceCapability.supportsRealtime || streaming || voiceInput.recording || voiceInput.transcribing}
              title={liveConnected ? 'Disconnect live assistant' : 'Connect live assistant'}
            >
              {liveSessionState === 'connecting'
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <Zap className="h-4 w-4" />}
            </button>
            {liveConnected && (
              <button
                className={cn(
                  'p-1.5 rounded-lg transition-colors',
                  liveSessionState === 'listening'
                    ? 'bg-sky-600 text-white animate-pulse'
                    : liveSessionState === 'speaking'
                      ? 'bg-amber-100 text-amber-700'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
                onClick={() => { void handleLiveTurn(); }}
                disabled={streaming || voiceInput.recording || voiceInput.transcribing}
                title={
                  liveSessionState === 'speaking'
                    ? 'Interrupt assistant'
                    : liveSessionState === 'listening'
                      ? 'Stop live turn'
                      : 'Start live assistant turn'
                }
              >
                {liveSessionState === 'speaking'
                  ? <VolumeX className="h-4 w-4" />
                  : liveSessionState === 'listening'
                    ? <MicOff className="h-4 w-4" />
                    : <Bot className="h-4 w-4" />}
              </button>
            )}
            {voiceInput.recording && (
              <>
                <span className="text-[10px] font-mono text-red-500 tabular-nums min-w-[2ch]">
                  {voiceInput.elapsed}s
                </span>
                <span className="flex gap-0.5 items-center">
                  {[0, 1, 2].map(i => (
                    <span
                      key={i}
                      className="w-0.5 bg-red-500 rounded-full animate-pulse"
                      style={{
                        height: `${8 + Math.random() * 8}px`,
                        animationDelay: `${i * 150}ms`,
                      }}
                    />
                  ))}
                </span>
              </>
            )}
            <button
              className={cn(
                'p-1.5 rounded-lg transition-colors',
                voiceInput.recording
                  ? 'bg-red-500 text-white animate-pulse'
                  : voiceInput.transcribing
                    ? 'text-muted-foreground cursor-wait'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
              onClick={() => {
                if (voiceInput.transcribing || streaming) return;
                if (voiceInput.recording) {
                  voiceInput.stopRecording();
                } else {
                  void voiceInput.startRecording();
                }
              }}
              disabled={voiceInput.transcribing || streaming}
              title={voiceInput.recording ? t('chat.stopRecording', 'Tap to stop') : t('chat.voiceInput', 'Tap to speak')}
            >
              {voiceInput.transcribing
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : voiceInput.recording
                  ? <MicOff className="h-4 w-4" />
                  : <Mic className="h-4 w-4" />}
            </button>
          </div>
          )}

          {/* Send / Stop button */}
          {streaming ? (
            <button
              className="p-1.5 rounded-lg shrink-0 transition-colors bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={cancelStream}
              title={t('chat.stop', 'Stop generating')}
            >
              <Square className="h-4 w-4" />
            </button>
          ) : (
            <button
              className={cn(
                'p-1.5 rounded-lg shrink-0 transition-colors',
                (input.trim() || pendingFiles.length > 0)
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                  : 'text-muted-foreground bg-muted cursor-not-allowed',
              )}
              onClick={() => void handleSend()}
              disabled={uploading || (!input.trim() && pendingFiles.length === 0)}
              title="Send (Enter)"
            >
              <Send className="h-4 w-4" />
            </button>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground mt-1 px-1">
          {t('chat.disclaimer', 'AI responses may not be accurate. Always verify critical information.')}
        </p>
      </div>

      {/* ── Share dialog overlay ── */}
      {shareOpen && (
        <>
          <div className="absolute inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={() => setShareOpen(false)} />
          <div className="absolute inset-0 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-sm rounded-xl border bg-background shadow-2xl">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <div className="flex items-center gap-2">
                  <Share2 className="h-4 w-4 text-primary" />
                  <span className="text-sm font-semibold">{t('chat.shareTitle', 'Share Conversation')}</span>
                </div>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setShareOpen(false)}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>

              <div className="p-4 space-y-4">
                {/* Q+A pair selector */}
                {qaPairs.length > 1 && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center justify-between">
                      <span>
                        <MessageSquare className="inline h-3 w-3 mr-1" />
                        {t('chat.selectMessages', 'Include messages')}
                      </span>
                      <span className="text-[10px] font-normal">
                        {qaPairs.length - excludedPairs.size}/{qaPairs.length}
                      </span>
                    </label>
                    <div className="max-h-36 overflow-y-auto space-y-1 rounded-lg border p-1.5">
                      {qaPairs.map((pair) => {
                        const included = !excludedPairs.has(pair.index);
                        const preview = pair.user.content.length > 50
                          ? pair.user.content.slice(0, 50) + '…'
                          : pair.user.content;
                        return (
                          <button
                            key={pair.index}
                            className={cn(
                              'w-full flex items-start gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors',
                              included
                                ? 'bg-primary/5 hover:bg-primary/10'
                                : 'bg-muted/30 opacity-50 hover:opacity-70',
                            )}
                            onClick={() => togglePair(pair.index)}
                          >
                            <div className={cn(
                              'mt-0.5 h-3.5 w-3.5 shrink-0 rounded border flex items-center justify-center transition-colors',
                              included ? 'bg-primary border-primary' : 'border-muted-foreground/40',
                            )}>
                              {included && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="truncate font-medium">{preview}</p>
                              {pair.assistant && (
                                <p className="truncate text-muted-foreground mt-0.5">
                                  AI: {pair.assistant.content.slice(0, 40)}…
                                </p>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Expiry selector */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                    <Clock className="inline h-3 w-3 mr-1" />
                    {t('chat.shareExpiry', 'Link expires after')}
                  </label>
                  <div className="flex gap-1.5">
                    {[
                      { label: '15m', value: '15m' },
                      { label: '1h', value: '1h' },
                      { label: '24h', value: '24h' },
                      { label: '7d', value: '7d' },
                    ].map(opt => (
                      <button key={opt.value}
                        className={cn(
                          'flex-1 rounded-lg py-1.5 text-xs font-medium transition-colors border',
                          shareExpiry === opt.value
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-muted/50 hover:bg-muted border-transparent',
                        )}
                        onClick={() => setShareExpiry(opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Generate / URL display */}
                {!shareUrl ? (
                  <Button className="w-full" onClick={() => void handleShare()} disabled={shareLoading}>
                    {shareLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Share2 className="h-4 w-4 mr-2" />}
                    {t('chat.generateLink', 'Generate Share Link')}
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 rounded-lg border bg-muted/30 p-2">
                      <input
                        readOnly
                        value={shareUrl}
                        className="flex-1 bg-transparent text-xs outline-none truncate"
                        onFocus={e => e.target.select()}
                      />
                      <Button size="sm" variant="outline" className="h-7 px-2 shrink-0" onClick={() => void handleCopyShareUrl()}>
                        {shareCopied
                          ? <><Check className="h-3 w-3 mr-1 text-green-600" /> {t('chat.copied', 'Copied!')}</>
                          : <><Copy className="h-3 w-3 mr-1" /> {t('chat.copy', 'Copy')}</>
                        }
                      </Button>
                    </div>
                    {/* WhatsApp share */}
                    <Button
                      size="sm"
                      className="w-full h-8 bg-[#25D366] hover:bg-[#1DA851] text-white gap-2"
                      onClick={() => {
                        const text = encodeURIComponent(`${t('chat.sharedConversation', 'Shared conversation')}:\n${shareUrl}`);
                        window.open(`https://wa.me/?text=${text}`, '_blank');
                      }}
                    >
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      {t('chat.shareWhatsApp', 'Share via WhatsApp')}
                    </Button>
                    <p className="text-[10px] text-muted-foreground">
                      {t('chat.shareInfo', 'Anyone with this link can view this conversation without logging in.')}
                      {' '}{t('chat.expiresAt', 'Expires')}: {new Date(shareExpiresAt).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Quick prompt management modal ── */}
      {managePromptsOpen && (
        <QuickPromptManager
          prompts={quickPrompts}
          onSave={(updated) => { setQuickPrompts(updated); saveQuickPrompts(updated); }}
          onClose={() => setManagePromptsOpen(false)}
        />
      )}
    </div>
  );

  // ── Desktop layout ──────────────────────────────
  if (mode === 'desktop') {
    return (
      <>
        <div className="flex h-[calc(100vh-6rem)] min-h-0 gap-0 rounded-xl border overflow-hidden">
          {/* Session list */}
          <div className="w-56 shrink-0 border-r bg-muted/20">
            {sidebarContent}
          </div>
          {/* Chat */}
          <div className="flex-1 min-w-0 flex flex-col min-h-0">
            {chatContent}
          </div>
        </div>
        {auditAction && (
          <HmsJobAuditModal
            action={auditAction}
            onClose={() => setAuditAction(null)}
            onConfirm={(correctedData) => {
              setAuditAction(null);
              // Build a correction message with serialized field values
              const lines = Object.entries(correctedData)
                .filter(([, v]) => v?.trim())
                .map(([k, v]) => `${k}: ${v}`);
              void sendMessage(
                `[HMS_JOB_CONFIRM] Please create the HMS import job with these confirmed fields:\n${lines.join('\n')}`,
                { targetConvId: activeId ?? undefined, responseLanguage },
              );
            }}
          />
        )}
      </>
    );
  }

  // ── Mobile layout ──────────────────────────────
  return (
    <>
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden relative">
        {chatContent}

        {/* Mobile sidebar overlay */}
        {showSidebar && (
          <>
            <div className="absolute inset-0 z-40 bg-black/40" onClick={() => setShowSidebar(false)} />
            <div className="absolute left-0 top-0 bottom-0 z-50 w-64 bg-background border-r shadow-xl">
              {sidebarContent}
            </div>
          </>
        )}
      </div>
      {auditAction && (
        <HmsJobAuditModal
          action={auditAction}
          onClose={() => setAuditAction(null)}
          onConfirm={(correctedData) => {
            setAuditAction(null);
            const lines = Object.entries(correctedData)
              .filter(([, v]) => v?.trim())
              .map(([k, v]) => `${k}: ${v}`);
            void sendMessage(
              `[HMS_JOB_CONFIRM] Please create the HMS import job with these confirmed fields:\n${lines.join('\n')}`,
              { targetConvId: activeId ?? undefined, responseLanguage },
            );
          }}
        />
      )}
    </>
  );
}
